/* Ficha de bolsas personalizadas (CRO 2026-10).
 * - Precio por bolsa = precio de la variante ÷ valor de la opción "Cantidad…".
 * - Botón de WhatsApp con mensaje prellenado (producto, variante, cantidad).
 * - Variante agotada: el botón "Agotado" se cambia por "Pídela por WhatsApp".
 * - Barra fija inferior en móvil con precio y botón de agregar.
 * - Precio de referencia en dólares (USDT) para Venezuela si window.AseoUSD está activo (snippets/moneda-usd.liquid).
 * Escucha el evento variantChange de Dawn (pubsub.js) y no toca precios ni inventario.
 */
if (!customElements.get('personalizada-pdp')) {
  customElements.define(
    'personalizada-pdp',
    class PersonalizadaPdp extends HTMLElement {
      connectedCallback() {
        try {
          this.datos = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        } catch (e) {
          return;
        }
        this.sectionId = this.dataset.section;
        this.seccion = document.getElementById(`MainProduct-${this.sectionId}`) || document;
        this.idxCantidad = parseInt(this.dataset.opcionCantidad || '0', 10) - 1;
        this.formato = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
        this.textos = {
          paquete: this.dataset.textoPaquete || 'Paquete de {cantidad} bolsas',
          unitario: this.dataset.textoUnitario || '{precio} por bolsa',
          ahorro: this.dataset.textoAhorro || 'Desde {precio} por bolsa pidiendo {cantidad}',
          mensaje: this.dataset.mensaje || 'Hola, quiero cotizar {producto}, {variante} ({cantidad} bolsas).',
          mensajeAgotado: this.dataset.mensajeAgotado || 'Hola, quiero pedir {producto}, {variante} ({cantidad} bolsas). En la web aparece agotada.',
          unidad: this.dataset.textoUnidad || 'Precio por bolsa. Pide las que necesites.',
          unidadMinimo: this.dataset.textoUnidadMinimo || 'Precio por bolsa. Pedido mínimo de {minimo} bolsas.',
          totalPaquetes: this.dataset.textoTotalPaquetes || '{paquetes} paquetes = {bolsas} bolsas · {total}',
          totalUnidades: this.dataset.textoTotalUnidades || '{bolsas} bolsas · {total}',
          etiquetaPaquetes: this.dataset.etiquetaPaquetes || '',
          etiquetaUnidades: this.dataset.etiquetaUnidades || '',
        };
        this.seccion.querySelectorAll('quantity-input input').forEach((inp) => {
          ['change', 'input'].forEach((ev) => inp.addEventListener(ev, () => this.actualizarTotal(this.varianteActual || {})));
        });
        this.minimo = parseInt(this.dataset.minimo || '0', 10) || 0;
        this.whatsapp = (this.dataset.whatsapp || '').replace(/\D/g, '');
        this.producto = this.dataset.producto || '';

        const inicial = this.datos.variantes.find((v) => v.id === this.datos.seleccionada) || this.datos.variantes[0];
        this.actualizar(inicial);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (evento) => {
            const v = evento?.data?.variant;
            if (!v) return this.sinVariante();
            const local = this.datos.variantes.find((x) => x.id === v.id);
            this.actualizar(local || { id: v.id, precio: v.price, disponible: v.available, opciones: v.options, titulo: v.title });
          });
        }
        this.montarBarraFija();
        this.alCambiarMoneda = () => this.varianteActual && this.actualizar(this.varianteActual);
        document.addEventListener('aseo:moneda', this.alCambiarMoneda);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.observador) this.observador.disconnect();
        if (this.alCambiarMoneda) document.removeEventListener('aseo:moneda', this.alCambiarMoneda);
      }

      usdActivo() {
        return !!(window.AseoUSD && window.AseoUSD.activo);
      }

      // " · ≈ US$ 0,36" si el visitante ve precios en dólares; vacío si no o si da menos de `minimo` dólares
      usd(pesos, minimo = 0) {
        if (!this.usdActivo() || !(pesos > 0) || pesos / window.AseoUSD.tasa < minimo) return '';
        return ` · ≈ ${window.AseoUSD.formatear(pesos)}`;
      }

      // bolsas por paquete en lisas: metacampo cro.unidades_paquete, o "80 und" en el valor de la opción, o 1
      unidadesDe(variante) {
        if (variante.unidades && variante.unidades > 0) return variante.unidades;
        const m = (variante.opciones || []).join(' ').match(/(\d+)\s*und/i);
        return m ? parseInt(m[1], 10) : 1;
      }

      esLisa() {
        return this.idxCantidad < 0;
      }

      cantidadSelector() {
        const inp = this.seccion.querySelector(`#Quantity-${this.sectionId}`) || this.seccion.querySelector('quantity-input input');
        const n = inp ? parseInt(inp.value, 10) : 1;
        return isNaN(n) || n < 1 ? 1 : n;
      }

      cantidadDe(variante) {
        if (this.esLisa()) return this.unidadesDe(variante);
        if (!variante.opciones) return 0;
        const n = parseInt(String(variante.opciones[this.idxCantidad]).replace(/\D/g, ''), 10);
        return isNaN(n) ? 0 : n;
      }

      porBolsa(variante) {
        const q = this.cantidadDe(variante);
        return q > 0 ? Math.round(variante.precio / 100 / q) : null;
      }

      textoCantidadWa(variante) {
        if (!this.esLisa()) return String(this.cantidadDe(variante) || '');
        const u = this.unidadesDe(variante);
        const n = this.cantidadSelector();
        return u > 1 ? `${n} paquete${n === 1 ? '' : 's'} de ${u} bolsas` : `${n} bolsa${n === 1 ? '' : 's'}`;
      }

      actualizarTotal(variante) {
        const total = this.seccion.querySelector(`#PrecioBolsa-${this.sectionId} .precio-bolsa__total`);
        const n = this.cantidadSelector();
        const u = this.unidadesDe(variante);
        const pesos = (variante.precio / 100) * n;
        const monto = this.formato.format(pesos) + this.usd(pesos);
        if (total) {
          if (this.esLisa() && variante.disponible !== false) {
            const plantilla = u > 1 ? this.textos.totalPaquetes : this.textos.totalUnidades;
            let texto = plantilla
              .replace('{paquetes}', n.toLocaleString('es-CO'))
              .replace('{bolsas}', (n * u).toLocaleString('es-CO'))
              .replace('{total}', monto);
            if (n === 1) texto = texto.replace(/\bpaquetes\b/, 'paquete');
            if (n * u === 1) texto = texto.replace(/\bbolsas\b/, 'bolsa');
            total.textContent = texto;
            total.classList.toggle('hidden', n <= 1 && u <= 1);
          } else {
            total.classList.add('hidden');
          }
        }
        const barra = this.seccion.querySelector(`#BarraFija-${this.sectionId}`);
        if (barra && this.esLisa()) {
          barra.querySelector('.sticky-atc__precio').textContent = this.formato.format(pesos);
          if (this.usdActivo()) barra.querySelector('.sticky-atc__unitario').textContent = `≈ ${window.AseoUSD.formatear(pesos)}`;
        }
        const etiqueta = this.seccion.querySelector('.quantity__label > span[aria-hidden="true"]:not(.quantity__rules-cart)');
        if (etiqueta && this.esLisa()) {
          const texto = u > 1 ? this.textos.etiquetaPaquetes : this.textos.etiquetaUnidades;
          if (texto) etiqueta.textContent = u <= 1 && this.minimo > 1 ? `${texto} (mínimo ${this.minimo.toLocaleString('es-CO')})` : texto;
        }
      }

      mejorEscalon(variante) {
        // misma combinación salvo la cantidad: el escalón con menor precio por bolsa
        if (this.idxCantidad < 0) return null;
        const resto = (o) => o.filter((_, i) => i !== this.idxCantidad).join('|');
        const clave = resto(variante.opciones);
        let mejor = null;
        this.datos.variantes.forEach((v) => {
          if (resto(v.opciones) !== clave) return;
          const p = this.porBolsa(v);
          if (p !== null && (mejor === null || p < mejor.precio)) mejor = { precio: p, cantidad: this.cantidadDe(v), id: v.id };
        });
        return mejor && mejor.id !== variante.id ? mejor : null;
      }

      urlWhatsApp(variante, agotado) {
        const plantilla = agotado ? this.textos.mensajeAgotado : this.textos.mensaje;
        const texto = plantilla
          .replace('{producto}', this.producto)
          .replace('{variante}', variante.titulo || '')
          .replace('{cantidad}', String(this.cantidadDe(variante) || ''));
        return `https://wa.me/${this.whatsapp}?text=${encodeURIComponent(texto)}`;
      }

      urlWhatsAppLisa(variante, agotado) {
        const plantilla = agotado ? this.textos.mensajeAgotado : this.textos.mensaje;
        const texto = plantilla
          .replace('{producto}', this.producto)
          .replace('{variante}', variante.titulo || '')
          .replace('{cantidad}', this.textoCantidadWa(variante));
        return `https://wa.me/${this.whatsapp}?text=${encodeURIComponent(texto)}`;
      }

      actualizar(variante) {
        this.varianteActual = variante;
        const q = this.cantidadDe(variante);
        const por = this.porBolsa(variante);
        const agotado = variante.disponible === false;
        let paquete = q > 0 ? this.textos.paquete.replace('{cantidad}', q.toLocaleString('es-CO')) : '';
        let unitario = por !== null ? this.textos.unitario.replace('{precio}', this.formato.format(por)) + this.usd(por, 0.1) : '';
        if (this.esLisa() && q <= 1) {
          paquete = '';
          unitario = this.minimo > 1 ? this.textos.unidadMinimo.replace('{minimo}', this.minimo.toLocaleString('es-CO')) : this.textos.unidad;
        }

        const cont = this.seccion.querySelector(`#PrecioBolsa-${this.sectionId}`);
        if (cont) {
          cont.querySelector('.precio-bolsa__paquete').textContent = paquete;
          cont.querySelector('.precio-bolsa__unitario').textContent = unitario;
          const ahorro = cont.querySelector('.precio-bolsa__ahorro');
          if (ahorro) {
            const mejor = this.mejorEscalon(variante);
            if (mejor && por !== null && mejor.precio < por && mejor.cantidad !== q) {
              ahorro.textContent = this.textos.ahorro
                .replace('{precio}', this.formato.format(mejor.precio))
                .replace('{cantidad}', mejor.cantidad.toLocaleString('es-CO'));
              ahorro.classList.remove('hidden');
            } else {
              ahorro.classList.add('hidden');
            }
          }
        }

        // WhatsApp: enlaces secundarios y de agotado
        const url = this.esLisa() ? this.urlWhatsAppLisa(variante, false) : this.urlWhatsApp(variante, false);
        const urlAgotado = this.esLisa() ? this.urlWhatsAppLisa(variante, true) : this.urlWhatsApp(variante, true);
        this.actualizarTotal(variante);
        this.seccion.querySelectorAll('.wa-link').forEach((a) => (a.href = url));
        this.seccion.querySelectorAll('.wa-agotado').forEach((a) => (a.href = urlAgotado));

        // agotado → botón de WhatsApp en vez de "Agotado"
        const submit = this.seccion.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (submit) submit.classList.toggle('hidden', agotado);
        this.seccion.querySelectorAll('.wa-agotado').forEach((a) => a.classList.toggle('hidden', !agotado));
        const pago = this.seccion.querySelector('.shopify-payment-button');
        if (pago) pago.classList.toggle('hidden', agotado);

        // barra fija
        const barra = this.seccion.querySelector(`#BarraFija-${this.sectionId}`);
        if (barra) {
          barra.querySelector('.sticky-atc__precio').textContent = this.formato.format(variante.precio / 100);
          barra.querySelector('.sticky-atc__unitario').textContent = this.usdActivo() ? `≈ ${window.AseoUSD.formatear(variante.precio / 100)}` : unitario;
          barra.querySelector('.sticky-atc__btn').classList.toggle('hidden', agotado);
          const wa = barra.querySelector('.sticky-atc__wa');
          wa.href = urlAgotado;
          wa.classList.toggle('hidden', !agotado);
        }
      }

      sinVariante() {
        const cont = this.seccion.querySelector(`#PrecioBolsa-${this.sectionId}`);
        if (cont) {
          cont.querySelector('.precio-bolsa__paquete').textContent = '';
          cont.querySelector('.precio-bolsa__unitario').textContent = '';
        }
      }

      montarBarraFija() {
        const barra = this.seccion.querySelector(`#BarraFija-${this.sectionId}`);
        const submit = this.seccion.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!barra || !submit) return;
        barra.querySelector('.sticky-atc__btn').addEventListener('click', () => {
          const form = document.getElementById(this.dataset.form);
          if (!form) return;
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        });
        const ancla = this.seccion.querySelector('.product-form__buttons') || submit;
        this.observador = new IntersectionObserver(
          (entradas) => {
            const visible = entradas.some((e) => e.isIntersecting);
            const porDebajo = ancla.getBoundingClientRect().top > window.innerHeight;
            barra.classList.toggle('sticky-atc--visible', !visible && !porDebajo);
          },
          { threshold: 0 }
        );
        this.observador.observe(ancla);
      }
    }
  );
}
